import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import { t, tOr } from '../i18n'
import { Notice, Pending } from '../ui'
import { canRememberDevice, hardwareId } from './device'
import { useSession } from './session'

/**
 * The trailer tablet: a project's people, a name, and a PIN.
 *
 * This is the screen Q-190 asks for, and the one thing about it worth reading
 * before changing anything is **where it sits**. Every other screen in this
 * application is behind `RequireSession`, which sends anybody without a
 * credential to `/signin`. This one is the request that *makes* a credential,
 * so it is a route of its own outside that gate — the same shape the API has
 * for the same reason, where sign-in is platform code because a module's routes
 * all depend on `get_person_id`.
 *
 * **It shows the crew list before anybody has proved anything, and enrolment is
 * what makes that safe.** A tablet the instance has never admitted is told
 * nothing at all: not the project, not who is on it. So an unenrolled tablet
 * shows its own identifier and asks for an administrator, which is the only
 * useful thing it can say and is not a leak — the identifier is minted by this
 * browser and means nothing until somebody admits it.
 *
 * **What happens after a PIN is taken is nothing special**, and that is the
 * design rather than an omission. The token comes back marked `sfd_`, the
 * instance resolves it at the identity door ahead of whichever provider the box
 * is configured for, and the person lands in the ordinary shell on the device's
 * own project. A tablet is a way of signing in, not a separate application.
 */

const PIN_LENGTH = 6
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const

type Refusal =
  /** The code was looked at and was wrong, or the person has no PIN. One answer. */
  | { kind: 'refused' }
  /** The keypad has not looked at the code yet. Q-201: it is still waiting. */
  | { kind: 'waiting'; seconds: number }
  | null

export function DeviceSignIn() {
  const { credentials, signIn } = useSession()
  const navigate = useNavigate()
  const [device] = useState(() => hardwareId())
  // Whether somebody was *already* signed in when this screen opened, decided
  // once and never again.
  //
  // Reading `credentials` at render time would be the same thing today and
  // only by luck: a sign-in that has just worked sets them too, and the guard
  // below is saved from firing on the way out purely because the router has
  // already left `/device` by then and unmounted this. That is an ordering
  // nobody would think to preserve. Deciding it at mount says what the guard
  // actually means — somebody arrived here already signed in — rather than
  // depending on which of two redirects lands first.
  const [arrivedSignedIn] = useState(() => credentials !== null)
  const [remembers] = useState(() => canRememberDevice())
  const [personId, setPersonId] = useState('')
  const [pin, setPin] = useState('')
  const [refusal, setRefusal] = useState<Refusal>(null)
  const [sending, setSending] = useState(false)

  const enrolment = useQuery({
    queryKey: ['signin', 'device', device],
    retry: false,
    enabled: remembers,
    queryFn: async () => {
      const result = await api.GET('/signin/device', {
        params: { query: { hardware_id: device } },
      })
      // A 404 is the ordinary answer for a tablet nobody has admitted yet, not
      // a failure: it is what the screen below is mostly for. Anything else is
      // a real fault and is thrown so the notice says so.
      if (result.response.status === 404) return null
      if (result.data === undefined) throw new Error(`HTTP ${result.response.status}`)
      return result.data
    },
  })

  // The wait counts itself down on the screen. A number that sits still reads
  // as a tablet that has frozen, and somebody who cannot see the wait shrinking
  // goes and finds an administrator to reset a PIN that was never the problem.
  useEffect(() => {
    if (refusal?.kind !== 'waiting') return
    if (refusal.seconds <= 0) {
      setRefusal(null)
      return
    }
    const timer = setTimeout(
      () => setRefusal({ kind: 'waiting', seconds: refusal.seconds - 1 }),
      1000,
    )
    return () => clearTimeout(timer)
  }, [refusal])

  const waiting = refusal?.kind === 'waiting' ? refusal.seconds : 0

  async function submit(code: string): Promise<void> {
    setSending(true)
    try {
      const result = await api.POST('/signin/device', {
        body: { hardware_id: device, person_id: personId, pin: code },
      })
      if (result.data !== undefined) {
        signIn({ mode: 'device', token: result.data.token })
        // Straight to the device's own project rather than through the landing
        // route: this tablet belongs to one job, and the answer just said which.
        navigate(`/p/${result.data.project_id}`, { replace: true })
        return
      }
      setPin('')
      if (result.response.status === 429) {
        setRefusal({ kind: 'waiting', seconds: retryAfter(result) })
        return
      }
      setRefusal({ kind: 'refused' })
    } finally {
      setSending(false)
    }
  }

  function press(digit: string): void {
    if (sending || waiting > 0) return
    const next = `${pin}${digit}`.slice(0, PIN_LENGTH)
    setRefusal(null)
    setPin(next)
    // Sent on the last digit, the way a phone does it. There is no button to
    // press: a six-digit code is complete or it is not, and asking somebody in
    // gloves to find a second target after the sixth digit is a tap that does
    // nothing but fail to land.
    if (next.length === PIN_LENGTH) void submit(next)
  }

  // Already signed in when this screen opened. Sending that person back to the
  // keypad would ask them to prove who they are twice. Rendered as a
  // redirection rather than called: navigating from inside a render is a side
  // effect in the one place React does not allow one, and it re-enters this
  // component before the router has moved — which is a loop and not a redirect,
  // and it hangs a test runner rather than failing it.
  if (arrivedSignedIn) return <Navigate to="/" replace />

  if (!remembers) {
    return (
      <Frame>
        <Notice tone="bad">{t('device.no_storage')}</Notice>
      </Frame>
    )
  }

  if (enrolment.isPending) {
    return (
      <Frame>
        <Pending />
      </Frame>
    )
  }

  // The error decides only where there is no crew list to keep. This query is
  // declared `retry: false`, takes the application's 30-second staleTime and
  // leaves `refetchOnReconnect` at its default, so a link that drops for a
  // moment is enough to refetch — and a proxy answering 502 while the
  // application comes back has said nothing whatever about the list already on
  // the glass. Deciding on the error first replaced the keypad, and the dots
  // with it, for somebody in gloves four digits into a six-digit code.
  if (enrolment.error && !enrolment.data) {
    return (
      <Frame>
        <Notice tone="bad">{t('device.unreachable')}</Notice>
        <p className="muted">
          <button type="button" onClick={() => void enrolment.refetch()}>
            {t('app.retry')}
          </button>
        </p>
      </Frame>
    )
  }

  // Kept screen, stated failure. A refresh that failed silently would be worse
  // than the unmount it replaces, so the tablet says the list is the last one
  // it received — in its own words rather than through `ErrorNotice`, which
  // renders the status and is written for somebody at a desk. Nobody standing
  // at a keypad can act on an HTTP code.
  const stale = enrolment.error ? <Notice tone="warn">{t('device.list_stale')}</Notice> : null

  if (enrolment.data === null) {
    return (
      <Frame>
        <Notice tone="warn">{t('device.not_enrolled')}</Notice>
        <p className="muted">{t('device.not_enrolled_explain')}</p>
        <div className="field">
          <label htmlFor="hardware-id">{t('device.identifier')}</label>
          {/* Selectable rather than a button that copies: half of these are read
              down a phone line to somebody in an office, and a copy button is
              no help at all to that. */}
          <input id="hardware-id" className="mono" readOnly value={device} />
        </div>
      </Frame>
    )
  }

  const chosen = enrolment.data.people.find((person) => person.id === personId)

  if (!chosen) {
    return (
      <Frame project={enrolment.data.project_name}>
        <p className="page-sub">{t('device.who')}</p>
        {stale}
        {enrolment.data.people.length === 0 ? (
          <Notice tone="warn">{t('device.nobody')}</Notice>
        ) : (
          <ul className="people">
            {enrolment.data.people.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  className="person"
                  onClick={() => {
                    setPersonId(person.id)
                    setPin('')
                    setRefusal(null)
                  }}
                >
                  {person.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Frame>
    )
  }

  return (
    <Frame project={enrolment.data.project_name}>
      <p className="page-sub">{chosen.display_name}</p>

      {stale}
      {refusal?.kind === 'refused' ? (
        <Notice tone="bad">{tOr('signin.error.refused', t('device.refused'))}</Notice>
      ) : null}
      {waiting > 0 ? (
        <Notice tone="warn">{t('device.waiting', { seconds: String(waiting) })}</Notice>
      ) : null}

      <p className="pin-dots" role="status" aria-label={t('device.entered', { count: pin.length })}>
        {Array.from({ length: PIN_LENGTH }, (_unused, index) => (
          <span key={index} className="dot" data-filled={index < pin.length} />
        ))}
      </p>

      <div className="keypad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="key"
            disabled={sending || waiting > 0}
            onClick={() => press(key)}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="key"
          disabled={sending || waiting > 0 || pin.length === 0}
          onClick={() => setPin(pin.slice(0, -1))}
        >
          {t('device.backspace')}
        </button>
      </div>

      <p>
        <button
          type="button"
          onClick={() => {
            setPersonId('')
            setPin('')
            setRefusal(null)
          }}
        >
          {t('device.someone_else')}
        </button>
      </p>
    </Frame>
  )
}

function Frame({ project, children }: { project?: string; children: ReactNode }) {
  return (
    <div className="signin device">
      <h2 className="page">{t('app.name')}</h2>
      {project ? <p className="page-sub">{project}</p> : null}
      {children}
    </div>
  )
}

/**
 * How long the keypad said to wait.
 *
 * The header first, because `Retry-After` is the standard place and a proxy
 * that rewrites the body will leave it alone; the body second, because it is
 * what the route was written to carry. A refusal with neither is treated as the
 * shortest real wait rather than as none, so the screen never invites somebody
 * to hammer a keypad that has already refused them.
 */
function retryAfter(result: { error?: unknown; response: Response }): number {
  const header = Number(result.response.headers.get('Retry-After'))
  if (Number.isFinite(header) && header > 0) return header
  const detail = (result.error as { detail?: { retry_after?: unknown } } | undefined)?.detail
  const body = Number(detail?.retry_after)
  if (Number.isFinite(body) && body > 0) return body
  return 5
}
